import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Table
} from "reactstrap"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import swal from "sweetalert"
import { gethotelreservation, accepthotel, refusehotel } from "helpers/requests"

const statusConfig = {
  PENDING: { color: "warning", label: "En attente" },
  ACCEPTED: { color: "success", label: "Acceptée" },
  REFUSED: { color: "danger", label: "Refusée" },
  CANCELLED: { color: "secondary", label: "Annulée" },
  HOTEL_ACCEPTED: { color: "info", label: "Acceptée par l'hôtel" },
  HOTEL_REFUSED: { color: "dark", label: "Refusée par l'hôtel" },
}

const fmtDate = (d) => {
  if (!d) return "-"
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
}

const fmtDateTime = (d) => {
  if (!d) return "-"
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

const InfoItem = ({ label, value, badge }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12, color: "#74788d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
    {badge ? (
      <div>{value}</div>
    ) : (
      <div style={{ fontSize: 15, fontWeight: 500, color: "#343a40" }}>{value || "-"}</div>
    )}
  </div>
)

const HotelReservationDetail = (props) => {
  const [reservation, setReservation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false)
  const [showApplicants, setShowApplicants] = useState(false)
  const { id } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    document.title = "Détails de la Réservation Hôtel"
    const fetchReservationDetail = async () => {
      try {
        const response = await gethotelreservation(id)
        setReservation(response)
      } catch (error) {
        console.error("Error fetching reservation detail:", error)
        swal("Erreur", "Impossible de charger les détails de la réservation", "error")
        navigate('/hotel')
      } finally {
        setIsLoading(false)
      }
    }
    fetchReservationDetail()
  }, [id, navigate])

  const handleAccept = async () => {
    try {
      const acceptData = {
        email: reservation.user.email,
        name: reservation.user.firstname,
        surnom: reservation.user.lastname
      }
      await accepthotel(id, acceptData)
      swal({
        title: "Réservation Acceptée",
        text: "La réservation a été acceptée avec succès !",
        icon: "success",
      }).then(() => {
        window.location.href = '/hotel'
      })
    } catch (error) {
      console.error("Error accepting reservation:", error)
      swal("Erreur", "Impossible d'accepter la réservation", "error")
    }
  }

  const handleRefuse = async () => {
    try {
      const refuseData = {
        email: reservation.user.email,
        name: reservation.user.firstname,
        surnom: reservation.user.lastname
      }
      await refusehotel(id, refuseData)
      swal({
        title: "Réservation Refusée",
        text: "La réservation a été refusée.",
        icon: "warning",
      }).then(() => {
        window.location.href = '/hotel'
      })
    } catch (error) {
      console.error("Error refusing reservation:", error)
      swal("Erreur", "Impossible de refuser la réservation", "error")
    }
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <div className="spinner-border text-primary" role="status"></div>
        </Container>
      </div>
    )
  }

  if (!reservation) {
    return (
      <div className="page-content">
        <Container fluid className="text-center" style={{ paddingTop: 80 }}>
          <h4>Réservation introuvable</h4>
        </Container>
      </div>
    )
  }

  const applicants = JSON.parse(reservation.applicants || '[]')
  const sc = statusConfig[reservation.status] || statusConfig.PENDING

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Hôtels")}
            breadcrumbItem={props.t("Détails de la réservation")}
          />

          {/* Status + Amount header */}
          <Card style={{ borderLeft: `4px solid ${sc.color === "warning" ? "#f1b44c" : sc.color === "success" ? "#34c38f" : sc.color === "danger" ? "#f46a6a" : "#74788d"}`, marginBottom: 20 }}>
            <CardBody className="d-flex align-items-center justify-content-between flex-wrap" style={{ padding: "16px 24px" }}>
              <div className="d-flex align-items-center gap-3">
                <Badge color={sc.color} pill style={{ fontSize: 13, padding: "6px 16px" }}>
                  {sc.label}
                </Badge>
                <span style={{ color: "#74788d", fontSize: 13 }}>
                  Demande du {fmtDateTime(reservation.createdAt)}
                </span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#343a40" }}>
                {parseFloat(reservation.totalPrice || 0).toLocaleString("fr-FR")} DA
              </div>
            </CardBody>
          </Card>

          <Row>
            {/* Left column */}
            <Col lg={8}>
              {/* Stay details */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-building-house me-2" style={{ color: "#556ee6" }}></i>
                    Détails du séjour
                  </h5>
                  <Row>
                    <Col md={6}>
                      <InfoItem label="Date d'arrivée" value={fmtDate(reservation.checkinDate)} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Date de départ" value={fmtDate(reservation.checkoutDate)} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Nombre de nuits" value={reservation.totalNights} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Adultes" value={reservation.numAdults ?? "-"} />
                    </Col>
                    <Col md={4}>
                      <InfoItem label="Enfants" value={reservation.children || "0"} />
                    </Col>
                  </Row>
                  {reservation.property && (
                    <div
                      style={{
                        background: "#f8f9fa", borderRadius: 8, padding: "12px 16px",
                        cursor: "pointer", border: "1px solid #eff2f7"
                      }}
                      onClick={() => setIsPropertyModalOpen(true)}
                    >
                      <div style={{ fontSize: 12, color: "#74788d", textTransform: "uppercase", marginBottom: 4 }}>Propriété</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{reservation.property.name || "N/A"}</span>
                        <i className="bx bx-right-arrow-alt" style={{ color: "#556ee6" }}></i>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Booker info */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-user me-2" style={{ color: "#556ee6" }}></i>
                    Informations du réservant
                  </h5>
                  <Row>
                    <Col md={6}>
                      <InfoItem label="Nom" value={reservation.name} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Prénom" value={reservation.surnom} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Téléphone" value={reservation.phone} />
                    </Col>
                    <Col md={6}>
                      <InfoItem label="Adresse" value={`${reservation.address || ""}, ${reservation.zipcode || ""}`} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Applicants table */}
              {applicants.length > 0 && (
                <Card className="mb-3">
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 style={{ fontWeight: 600, fontSize: 16, margin: 0 }}>
                        <i className="bx bx-group me-2" style={{ color: "#556ee6" }}></i>
                        Occupants ({applicants.length})
                      </h5>
                      {applicants.length > 4 && (
                        <Button color="light" size="sm" onClick={() => setShowApplicants(true)}>
                          Voir tout
                        </Button>
                      )}
                    </div>
                    <div className="table-responsive">
                      <Table className="table-nowrap mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>#</th>
                            <th>Prénom</th>
                            <th>Nom</th>
                            <th>Date de naissance</th>
                            <th>Genre</th>
                            <th>Document</th>
                            <th>N° Document</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(applicants.length > 4 ? applicants.slice(0, 4) : applicants).map((a, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td style={{ fontWeight: 500 }}>{a.firstname || "-"}</td>
                              <td style={{ fontWeight: 500 }}>{a.lastname || "-"}</td>
                              <td>{a.birthday ? new Date(a.birthday).toLocaleDateString("fr-FR") : "-"}</td>
                              <td>{a.gender || "-"}</td>
                              <td>{a.documentType || "-"}</td>
                              <td>{a.documentNumber || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>

            {/* Right column */}
            <Col lg={4}>
              {/* Client account */}
              {reservation.user && (
                <Card className="mb-3">
                  <CardBody>
                    <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                      <i className="bx bx-id-card me-2" style={{ color: "#556ee6" }}></i>
                      Compte client
                    </h5>
                    <div className="d-flex align-items-center mb-3">
                      <div style={{
                        width: 42, height: 42, borderRadius: "50%",
                        background: "#556ee6", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 16, fontWeight: 600, marginRight: 12, flexShrink: 0
                      }}>
                        {(reservation.user.firstname?.[0] || "").toUpperCase()}{(reservation.user.lastname?.[0] || "").toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                          {reservation.user.firstname} {reservation.user.lastname}
                        </div>
                        <div style={{ color: "#74788d", fontSize: 13 }}>
                          {reservation.user.email}
                        </div>
                      </div>
                    </div>
                    {reservation.user.phone && (
                      <InfoItem label="Téléphone" value={reservation.user.phone} />
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Payment */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                    <i className="bx bx-money me-2" style={{ color: "#556ee6" }}></i>
                    Paiement
                  </h5>
                  <InfoItem label="Montant total" value={
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#343a40" }}>
                      {parseFloat(reservation.totalPrice || 0).toLocaleString("fr-FR")} DA
                    </span>
                  } />
                  <InfoItem label="Nombre de nuits" value={reservation.totalNights} />
                  <InfoItem label="Statut de paiement" badge value={
                    <Badge color={reservation.ispayed ? "success" : "warning"} pill style={{ fontSize: 12 }}>
                      {reservation.ispayed ? "Payé" : "Non payé"}
                    </Badge>
                  } />
                </CardBody>
              </Card>

              {/* Actions */}
              {reservation.status === "PENDING" && (
                <Card>
                  <CardBody>
                    <h5 className="mb-3" style={{ fontWeight: 600, fontSize: 16 }}>
                      <i className="bx bx-check-shield me-2" style={{ color: "#556ee6" }}></i>
                      Actions
                    </h5>
                    <div className="d-grid gap-2">
                      <Button color="success" onClick={handleAccept} style={{ fontWeight: 600 }}>
                        <i className="bx bx-check me-1"></i> Accepter la réservation
                      </Button>
                      <Button color="danger" outline onClick={handleRefuse} style={{ fontWeight: 600 }}>
                        <i className="bx bx-x me-1"></i> Refuser la réservation
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Property Modal */}
      <Modal isOpen={isPropertyModalOpen} toggle={() => setIsPropertyModalOpen(false)} centered>
        <ModalHeader toggle={() => setIsPropertyModalOpen(false)}>Détails de la propriété</ModalHeader>
        <ModalBody>
          <Row>
            <Col xs={6}>
              <InfoItem label="Code" value={reservation.property?.code} />
            </Col>
            <Col xs={6}>
              <InfoItem label="Type" value={reservation.property?.type} />
            </Col>
            <Col xs={6}>
              <InfoItem label="État" value={reservation.property?.state} />
            </Col>
            <Col xs={6}>
              <InfoItem label="Disponible" badge value={
                <Badge color={reservation.property?.available ? "success" : "danger"} pill>
                  {reservation.property?.available ? "Disponible" : "Non disponible"}
                </Badge>
              } />
            </Col>
            <Col xs={12}>
              <InfoItem label="Téléphone" value={reservation.property?.phonenumber} />
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* Full Applicants Modal */}
      <Modal isOpen={showApplicants} toggle={() => setShowApplicants(false)} size="lg">
        <ModalHeader toggle={() => setShowApplicants(false)}>
          Occupants ({applicants.length})
        </ModalHeader>
        <ModalBody>
          <Table responsive bordered hover>
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Date de naissance</th>
                <th>Genre</th>
                <th>Document</th>
                <th>N° Document</th>
                <th>Info supplémentaire</th>
              </tr>
            </thead>
            <tbody>
              {applicants.map((a, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{a.firstname || "-"}</td>
                  <td style={{ fontWeight: 500 }}>{a.lastname || "-"}</td>
                  <td>{a.birthday ? new Date(a.birthday).toLocaleDateString("fr-FR") : "-"}</td>
                  <td>{a.gender || "-"}</td>
                  <td>{a.documentType || "-"}</td>
                  <td>{a.documentNumber || "-"}</td>
                  <td>{a.additionalInfo || "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

HotelReservationDetail.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(HotelReservationDetail)
